import { useEffect, useState } from "react";
import { LogOut, Loader2, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRACK_NAV, ORG_TYPE_LABELS, COACH_ROLE_LABELS } from "./trackNav";
import TrackModule from "./TrackModule";

export default function TrackShell({ roleLabel }) {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("dashboard");

  useEffect(() => { api.get("/track/me").then(({ data }) => setMe(data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const org = me?.organization;
  const orgId = org?.id;
  const canWrite = me?.role === "org_admin" || (me?.role === "track_coach" && me?.coach_role !== "read_only");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded bg-[#1a1a1a] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">{org?.logo_url ? <img src={org.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 size={16} className="text-white/40" />}</div>
            <div className="min-w-0">
              <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 leading-none">{roleLabel}</p>
              <p className="font-display font-black uppercase tracking-tight truncate leading-tight">{org?.name || "Origin Track OS"}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="hidden sm:inline text-white/50 text-sm">{user?.name}{me?.coach_role ? ` · ${COACH_ROLE_LABELS[me.coach_role] || me.coach_role}` : ""}</span>
            <button data-testid="track-logout" onClick={logout} className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><LogOut size={14} /> <span className="hidden sm:inline">Log Out</span></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row">
        <aside className="md:w-60 md:border-r border-white/10 md:min-h-[calc(100vh-65px)]">
          <nav className="flex md:flex-col gap-1 p-3 overflow-x-auto" data-testid="track-nav">
            {TRACK_NAV.map((n) => {
              const Icon = n.icon;
              return (
                <button key={n.key} data-testid={`track-nav-${n.key}`} onClick={() => setActive(n.key)}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm whitespace-nowrap transition-colors text-left ${active === n.key ? "bg-white text-black font-bold" : "text-white/55 hover:text-white hover:bg-white/[0.04]"}`}>
                  <Icon size={16} /> {n.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-4 md:p-8">
          {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>
            : active === "dashboard" ? (
              <div data-testid="track-dashboard">
                <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">Welcome back</p>
                <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-6">{user?.name?.split(" ")[0] || "Coach"}</h1>
                {org ? (
                  <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8" data-testid="track-org-summary">
                    <div className="bg-[#0A0A0A] p-4"><p className="text-white/40 text-[10px] uppercase tracking-widest">Organization</p><p className="font-bold mt-1">{org.name}</p></div>
                    <div className="bg-[#0A0A0A] p-4"><p className="text-white/40 text-[10px] uppercase tracking-widest">Type</p><p className="font-bold mt-1">{ORG_TYPE_LABELS[org.org_type] || org.org_type}</p></div>
                    <div className="bg-[#0A0A0A] p-4"><p className="text-white/40 text-[10px] uppercase tracking-widest">Active Season</p><p className="font-bold mt-1">{org.active_season || "—"}</p></div>
                  </div>
                ) : (
                  <div className="border border-white/10 bg-[#121212] p-6 text-white/50 text-sm mb-8" data-testid="track-no-org">You haven't been assigned to an organization yet. Your Super Admin will connect your account soon.</div>
                )}
                {orgId && <TrackModule section="dashboard" orgId={orgId} canWrite={canWrite} />}
              </div>
            ) : <TrackModule section={active} orgId={orgId} canWrite={canWrite} />}
        </main>
      </div>
    </div>
  );
}
